import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Minus,
  Maximize2,
  Send,
  Paperclip,
  Loader2,
  File,
  ChevronDown,
  ChevronUp,
  Reply,
  Monitor,
  FolderOpen,
  AlertCircle,
} from "lucide-react";
import { useEmailCompose, ComposeAttachment } from "../../contexts/EmailComposeContext";
import SignatureEditor from "../ui/SignatureEditor";
import DocumentPickerModal from "./DocumentPickerModal";

// ── Helpers ──────────────────────────────────────────────────────────

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Component ────────────────────────────────────────────────────────

const EmailComposePopup: React.FC = () => {
  const {
    mode,
    draft,
    emailConnections,
    sending,
    sendError,
    clearSendError,
    uploadingAttachment,
    minimize,
    expand,
    close,
    updateDraft,
    uploadAttachments,
    removeAttachment,
    send,
  } = useEmailCompose();

  // Local UI state
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [showCcField, setShowCcField] = useState(false);
  const [showBccField, setShowBccField] = useState(false);
  const [draggingOver, setDraggingOver] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [subjectWarning, setSubjectWarning] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);

  // Close attach menu on outside click
  useEffect(() => {
    if (!showAttachMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showAttachMenu]);

  // ── Chip handlers ──────────────────────────────────────────────────

  const addChip = useCallback(
    (field: "to" | "cc" | "bcc", value: string) => {
      const email = value.trim().toLowerCase();
      if (!email || !email.includes("@")) return;
      const current = draft[field];
      if (!current.includes(email)) {
        updateDraft({ [field]: [...current, email] });
      }
    },
    [draft, updateDraft]
  );

  const removeChip = useCallback(
    (field: "to" | "cc" | "bcc", index: number) => {
      updateDraft({ [field]: draft[field].filter((_, i) => i !== index) });
    },
    [draft, updateDraft]
  );

  const handleChipKeyDown = useCallback(
    (
      field: "to" | "cc" | "bcc",
      input: string,
      setInput: (v: string) => void,
      e: React.KeyboardEvent
    ) => {
      if ((e.key === "Enter" || e.key === "," || e.key === "Tab") && input.trim()) {
        e.preventDefault();
        addChip(field, input);
        setInput("");
      }
      if (e.key === "Backspace" && !input && draft[field].length > 0) {
        removeChip(field, draft[field].length - 1);
      }
    },
    [draft, addChip, removeChip]
  );

  // ── Attachment handler ─────────────────────────────────────────────

  const handleFiles = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) uploadAttachments(e.target.files);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [uploadAttachments]
  );

  // ── Drag & drop ─────────────────────────────────────────────────────

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setDraggingOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setDraggingOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setDraggingOver(false);
      if (e.dataTransfer.files?.length) {
        uploadAttachments(e.dataTransfer.files);
      }
    },
    [uploadAttachments]
  );

  // ── Attach from documents ──────────────────────────────────────────

  const handleDocumentSelect = useCallback(
    (attachment: ComposeAttachment) => {
      updateDraft({
        attachments: [...draft.attachments, attachment],
      });
    },
    [draft.attachments, updateDraft]
  );

  // ── Close with confirm ─────────────────────────────────────────────

  const handleClose = useCallback(() => {
    const hasContent =
      draft.to.length > 0 ||
      draft.subject.trim() ||
      draft.attachments.length > 0 ||
      draft.bodyHtml.replace(/<br\s*\/?>/gi, "").replace(/<div[^>]*>[\s\S]*<\/div>/gi, "").trim().length > 0;

    if (hasContent) {
      setShowDiscardConfirm(true);
      return;
    }
    close();
  }, [draft, close]);

  // ── Send handler ───────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (!draft.subject.trim()) {
      setSubjectWarning(true);
      return;
    }
    setSubjectWarning(false);
    await send();
  }, [send, draft.subject]);

  // ── Title for minimized bar ────────────────────────────────────────

  const titleText = draft.replyToSubject
    ? `Re: ${draft.replyToSubject.replace(/^Re:\s*/i, "")}`
    : draft.subject || "New Email";

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <>
    <AnimatePresence>
      {mode !== "closed" && (
        <motion.div
          key={draft.draftId}
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={
            mode === "expanded"
              ? { opacity: 1, y: 0, scale: 1 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="fixed bottom-4 right-6 z-[1000] flex flex-col bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          style={{
            width: mode === "expanded" ? 580 : 320,
            maxHeight: mode === "expanded" ? "85vh" : 44,
            minHeight: mode === "expanded" ? 640 : 44,
          }}
        >
          {/* ── Title Bar ── */}
          <div
            className="flex items-center justify-between px-4 py-2.5 cursor-pointer select-none flex-shrink-0"
            style={{ backgroundColor: "var(--color-primary)" }}
            onClick={() => (mode === "minimized" ? expand() : minimize())}
          >
            <span className="text-sm font-semibold text-white truncate mr-4">
              {titleText}
            </span>
            <div className="flex items-center gap-1">
              {mode === "expanded" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    minimize();
                  }}
                  className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                  title="Minimize"
                >
                  <Minus className="h-4 w-4" />
                </button>
              )}
              {mode === "minimized" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    expand();
                  }}
                  className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                  title="Expand"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* ── Expanded body ── */}
          {mode === "expanded" && (
            <div
              className="flex flex-col flex-1 min-h-0 relative"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Discard confirmation overlay */}
              {showDiscardConfirm && (
                <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-sm rounded-b-xl flex items-center justify-center">
                  <div className="text-center px-8">
                    <div className="h-10 w-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-3">
                      <X className="h-5 w-5 text-red-500" />
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">Discard this draft?</p>
                    <p className="text-xs text-slate-400 mb-5">Your message will not be saved</p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setShowDiscardConfirm(false)}
                        className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Keep editing
                      </button>
                      <button
                        onClick={() => {
                          setShowDiscardConfirm(false);
                          close();
                        }}
                        className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Drop overlay */}
              {draggingOver && (
                <div className="absolute inset-0 z-50 bg-purple-50/90 border-2 border-dashed border-purple-400 rounded-b-xl flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <Paperclip className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-purple-700">Drop files to attach</p>
                  </div>
                </div>
              )}
              {/* Header fields */}
              <div className="px-4 pt-3 pb-1 space-y-1.5 flex-shrink-0 border-b border-slate-100">
                {/* Reply indicator */}
                {draft.replyToMessageId && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg mb-1">
                    <Reply className="h-3 w-3 text-purple-500 flex-shrink-0" />
                    <span className="text-[11px] text-purple-700 truncate flex-1">
                      Replying to: {draft.replyToSubject || "email"}
                    </span>
                    <button
                      onClick={() =>
                        updateDraft({
                          replyToMessageId: null,
                          replyToSubject: null,
                          subject: "",
                        })
                      }
                      className="text-purple-400 hover:text-purple-600 text-[11px] font-medium flex-shrink-0"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* From row */}
                {emailConnections.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-10 flex-shrink-0">From</span>
                    {emailConnections.length === 1 ? (
                      <span className="text-xs font-medium text-slate-600">
                        {emailConnections[0].mailbox.email}
                      </span>
                    ) : (
                      <select
                        value={draft.emailConnectionId || ""}
                        onChange={(e) => updateDraft({ emailConnectionId: e.target.value })}
                        className="text-xs font-medium text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer rounded-xl"
                      >
                        {emailConnections.map((ec) => (
                          <option key={ec._id} value={ec._id}>
                            {ec.mailbox.email}
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                      {!showCcField && (
                        <button
                          onClick={() => setShowCcField(true)}
                          className="text-[11px] text-slate-400 hover:text-slate-600 font-medium"
                        >
                          CC
                        </button>
                      )}
                      {!showBccField && (
                        <button
                          onClick={() => setShowBccField(true)}
                          className="text-[11px] text-slate-400 hover:text-slate-600 font-medium"
                        >
                          BCC
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* To row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400 w-10 flex-shrink-0">To</span>
                  {draft.to.map((email, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium"
                    >
                      {email}
                      <button
                        onClick={() => removeChip("to", i)}
                        className="hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={toInput}
                    onChange={(e) => setToInput(e.target.value)}
                    onKeyDown={(e) => handleChipKeyDown("to", toInput, setToInput, e)}
                    onBlur={() => {
                      if (toInput.trim()) {
                        addChip("to", toInput);
                        setToInput("");
                      }
                    }}
                    placeholder={draft.to.length === 0 ? "Add recipient..." : ""}
                    className="flex-1 min-w-[100px] text-xs border-none focus:outline-none bg-transparent"
                  />
                </div>

                {/* CC row */}
                {showCcField && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-400 w-10 flex-shrink-0">CC</span>
                    {draft.cc.map((email, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[11px]"
                      >
                        {email}
                        <button
                          onClick={() => removeChip("cc", i)}
                          className="hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={ccInput}
                      onChange={(e) => setCcInput(e.target.value)}
                      onKeyDown={(e) => handleChipKeyDown("cc", ccInput, setCcInput, e)}
                      onBlur={() => {
                        if (ccInput.trim()) {
                          addChip("cc", ccInput);
                          setCcInput("");
                        }
                      }}
                      placeholder="Add email..."
                      className="flex-1 min-w-[100px] text-xs border-none focus:outline-none bg-transparent"
                    />
                  </div>
                )}

                {/* BCC row */}
                {showBccField && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-400 w-10 flex-shrink-0">BCC</span>
                    {draft.bcc.map((email, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[11px]"
                      >
                        {email}
                        <button
                          onClick={() => removeChip("bcc", i)}
                          className="hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={bccInput}
                      onChange={(e) => setBccInput(e.target.value)}
                      onKeyDown={(e) => handleChipKeyDown("bcc", bccInput, setBccInput, e)}
                      onBlur={() => {
                        if (bccInput.trim()) {
                          addChip("bcc", bccInput);
                          setBccInput("");
                        }
                      }}
                      placeholder="Add email..."
                      className="flex-1 min-w-[100px] text-xs border-none focus:outline-none bg-transparent"
                    />
                  </div>
                )}

                {/* Subject */}
                <div className={`flex items-center gap-2 py-1 ${subjectWarning && !draft.subject.trim() ? "rounded-lg ring-1 ring-amber-400 bg-amber-50/50 px-1 -mx-1" : ""}`}>
                  <span className="text-xs text-slate-400 w-10 flex-shrink-0">Subj</span>
                  <input
                    type="text"
                    value={draft.subject}
                    onChange={(e) => {
                      updateDraft({ subject: e.target.value });
                      if (e.target.value.trim()) setSubjectWarning(false);
                    }}
                    placeholder="Add a subject..."
                    className="flex-1 text-sm font-medium border-none focus:outline-none bg-transparent text-slate-800 py-0.5"
                  />
                </div>
                {/* Subject warning */}
                {subjectWarning && !draft.subject.trim() && (
                  <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200 mt-1">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    <span className="text-[11px] text-amber-700">Add a subject line before sending</span>
                    <button
                      onClick={() => {
                        setSubjectWarning(false);
                        send();
                      }}
                      className="ml-auto text-[11px] font-medium text-amber-600 hover:text-amber-800 flex-shrink-0"
                    >
                      Send anyway
                    </button>
                  </div>
                )}
              </div>

              {/* ── Rich text editor ── */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <SignatureEditor
                  value={draft.bodyHtml}
                  onChange={(html) => updateDraft({ bodyHtml: html })}
                  placeholder="Write your email..."
                />
              </div>

              {/* ── Attachment chips ── */}
              {draft.attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-4 py-2 border-t border-slate-100 flex-shrink-0">
                  {draft.attachments.map((att, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-600"
                    >
                      <File className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate max-w-[120px]">{att.filename}</span>
                      <span className="text-slate-400">{formatFileSize(att.sizeBytes)}</span>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="hover:text-red-500 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* ── Send error (draft is kept) ── */}
              {sendError && (
                <div role="alert" className="flex items-start gap-2 px-4 py-2 border-t border-red-200 bg-red-50 flex-shrink-0">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-red-700 flex-1">{sendError}</span>
                  <button
                    onClick={clearSendError}
                    aria-label="Dismiss error"
                    className="text-red-400 hover:text-red-600 flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* ── Bottom toolbar ── */}
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                  {/* Send */}
                  <button
                    onClick={handleSend}
                    disabled={sending || !draft.bodyHtml.trim() || !draft.conversationId || draft.to.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold transition-colors disabled:opacity-40"
                    style={{ backgroundColor: "var(--color-primary)" }}
                    onMouseEnter={(e) => {
                      if (!sending && draft.bodyHtml.trim() && draft.conversationId && draft.to.length > 0) {
                        e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
                      }
                    }}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send
                      </>
                    )}
                  </button>

                  {/* Attach dropdown */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFiles}
                  />
                  <div className="relative" ref={attachMenuRef}>
                    <button
                      onClick={() => setShowAttachMenu(!showAttachMenu)}
                      disabled={uploadingAttachment}
                      className="inline-flex items-center gap-1 p-2 rounded-lg text-slate-500 hover:bg-slate-200 transition-colors disabled:opacity-40"
                      title="Attach files"
                    >
                      {uploadingAttachment ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Paperclip className="h-4 w-4" />
                          <ChevronUp className="h-3 w-3" />
                        </>
                      )}
                    </button>
                    {showAttachMenu && (
                      <div className="absolute bottom-full left-0 mb-1.5 bg-white rounded-lg shadow-lg border border-slate-200 py-1 w-52 z-50">
                        <button
                          onClick={() => {
                            setShowAttachMenu(false);
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                          <Monitor className="h-4 w-4 text-slate-400" />
                          Upload from computer
                        </button>
                        <button
                          onClick={() => {
                            setShowAttachMenu(false);
                            setShowDocPicker(true);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                          <FolderOpen className="h-4 w-4 text-slate-400" />
                          Attach from Documents
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Discard */}
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Discard"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>

    {/* Document picker modal */}
    <DocumentPickerModal
      open={showDocPicker}
      onClose={() => setShowDocPicker(false)}
      onSelect={handleDocumentSelect}
      clientId={draft.clientId}
    />
    </>
  );
};

export default EmailComposePopup;
